/**
 * PCM Encoder Utility
 * Convert audio formats for Azure OpenAI Realtime API
 */

/**
 * Convert Float32Array to Int16 PCM
 * CRITICAL: Must match Azure's expected format exactly
 */
export function float32ToInt16(float32: Float32Array): Int16Array {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    // Match reference implementation: 0x8000 for negative, 0x7FFF for positive
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16;
}

/**
 * Convert Int16Array to base64 string (for WebSocket transmission)
 * OPTIMIZED: Avoid spread operator for large arrays (matches reference implementation)
 */
export function int16ToBase64(int16: Int16Array): string {
  const uint8 = new Uint8Array(int16.length * 2);
  for (let i = 0; i < int16.length; i++) {
    // Little-endian byte order (LSB first, then MSB)
    uint8[i * 2] = int16[i] & 0xff;
    uint8[i * 2 + 1] = (int16[i] >> 8) & 0xff;
  }

  // Convert to binary string without spread operator (more reliable for large arrays)
  let binaryString = "";
  for (let i = 0; i < uint8.byteLength; i++) {
    binaryString += String.fromCharCode(uint8[i]);
  }

  return btoa(binaryString);
}

/**
 * Convert Float32Array directly to base64 (combines both operations)
 */
export function float32ToBase64(float32: Float32Array): string {
  const int16 = float32ToInt16(float32);
  return int16ToBase64(int16);
}

/**
 * Convert base64 to Int16Array (for receiving audio from WebSocket)
 * Uses DataView to ensure correct Little Endian decoding regardless of platform
 */
export function base64ToInt16(base64: string): Int16Array {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  // Use DataView to enforce Little Endian decoding (Azure standard)
  const int16 = new Int16Array(len / 2);
  const dataView = new DataView(bytes.buffer);

  for (let i = 0; i < int16.length; i++) {
    // getInt16(byteOffset, littleEndian)
    int16[i] = dataView.getInt16(i * 2, true);
  }

  return int16;
}

/**
 * Convert base64 to Float32Array (for audio playback)
 */
export function base64ToFloat32(base64: string): Float32Array {
  const int16 = base64ToInt16(base64);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float32;
}
